/* eslint-disable @typescript-eslint/no-misused-promises */
import { useCallback, useEffect, useRef } from 'react';

import { PRINTTY_ELEMENT_CLASS_ID, PRINT_FRAME_ID, PRINT_FRAME_ROOT_ID } from '../constants';
import { PrintConstructor } from '../printConstructor';
import { CreateTargetOptions } from '../types';
import { createDynamicSpacing } from '../utils/createDynamicSpacing';
import { frameSizeByOrientation, getFrameSizeInMm } from '../utils/getIframeSizingByOrientation';
import { injectDefaultPrinterStyles } from '../utils/injectDefaultPrinterStyles';
import { injectDefaultStyles } from '../utils/injectDefaultStyles';
import { useRenderer } from './useRenderer';

// basic observer configuration for the print target
const config = { attributes: false, childList: true, subtree: true };

/**
 * Hook to create a print target
 */
const useCreatePrinttyTarget = ({
  HeaderElement,
  HeaderInfoBoxElement,
  orientation = 'portrait',
  debug = false,
  headerProps,
  gap = 0,
  targetMargin = 0,
}: CreateTargetOptions = {}) => {
  /** The target iframe reference */
  const targetIframeRef = useRef<Window | null>(null);

  /** The html target ref we want to insert into the frame  */
  const targetRef = useRef<HTMLDivElement | null>(null);

  /** The react Node target ref we want to insert into the frame */
  const targetReactRef = useRef<React.ReactNode | null>(null);

  const { renderToTheVirtualDom, cleanUp } = useRenderer();

  const cleanupTargetFrame = useCallback(
    (overrideDebug = false) => {
      if (debug === true && !overrideDebug) {
        return;
      }
      const iFrame = document.getElementById(PRINT_FRAME_ID);
      if (iFrame) {
        document.body.removeChild(iFrame);
      }
      cleanUp();
    },
    [cleanUp, debug]
  );

  /**
   * Sets the targetReactRef
   */
  const setTargetReactRef = useCallback((node: React.ReactNode) => {
    targetReactRef.current = node;
  }, []);

  /**
  /**
   * Creates a target frame for rendering the print content
   * Once created inserts and renders the content from the targetRef
   *
   * @returns {HTMLIFrameElement} The created iframe element
   */
  const createTargetFrame = useCallback(() => {
    // Remove any existing frame before adding a new one
    cleanupTargetFrame();

    const targetFrame = document.createElement('iframe');

    // Structure the frame
    targetFrame.id = PRINT_FRAME_ID;
    targetFrame.srcdoc = '<!DOCTYPE html>';

    targetFrame.style.position = 'absolute';
    targetFrame.style.zIndex = '10000';

    if (debug == false) {
      targetFrame.style.top = `-${document.documentElement.clientHeight + 100}px`;
      targetFrame.style.left = `-${document.documentElement.clientWidth + 100}px`;
    } else {
      const { mm } = frameSizeByOrientation(orientation);
      targetFrame.style.top = `100px`;
      targetFrame.style.left = `100px`;
      targetFrame.style.width = `${mm.width}mm`;
      targetFrame.style.height = `${mm.height}mm`;
    }

    return targetFrame;
  }, [cleanupTargetFrame, debug, orientation]);

  const insertTarget = useCallback(
    async (printBody?: Document) => {
      return new Promise<HTMLDivElement>((resolve) => {
        const { rootDiv } = renderToTheVirtualDom(
          <PrintConstructor
            contentRef={targetRef}
            HeaderElement={HeaderElement}
            headerProps={headerProps}
            HeaderInfoBoxElement={HeaderInfoBoxElement}
            content={targetReactRef.current}
            targetMargin={targetMargin}
          />,
          printBody
        );

        rootDiv.id = PRINT_FRAME_ROOT_ID;

        return resolve(rootDiv);
      });
    },
    [HeaderElement, HeaderInfoBoxElement, headerProps, renderToTheVirtualDom, targetMargin]
  );

  /**
   * Injects both printing and default styles into the print body
   * This is needed so that any styles applied to the main document are also applied to the print document
   */
  const injectStyles = useCallback(
    async (printBody: Document) => {
      injectDefaultStyles(printBody, orientation, getFrameSizeInMm(orientation)); // Applies styles without media tags
      injectDefaultPrinterStyles(printBody, orientation); // Applies styles with media tags
    },
    [orientation]
  );

  /**
   * Fetches all the styles from the main document and returns them as a document fragment
   *
   * TODO: Load style sheets before resolving the promise
   * This will fix edge cases where the styles are not loaded in time for the print target to be created
   */
  const fetchStyles = useCallback(async () => {
    const fragment = new DocumentFragment();
    const arrStyleSheets = document.querySelectorAll(
      "style, link[rel~='stylesheet'], link[as='style']"
    );
    const styleSheets = Array.from(arrStyleSheets);
    await styleSheets.reduce(async (promise, styleSheet) => {
      await promise;
      fragment?.appendChild(styleSheet.cloneNode(true));
      return Promise.resolve();
    }, Promise.resolve());
    return fragment;
  }, []);

  /**
   * Creates a mutation observer for the print target
   * This is used to detect when the print target has successfully loaded the content
   */
  const createFrameObserver = useCallback(
    ({
      resolve,
      targetFrame,
      contentDocument,
      contentWindow,
    }: {
      resolve: (value: HTMLIFrameElement) => void;
      targetFrame: HTMLIFrameElement;
      contentDocument: Document;
      contentWindow: Window;
    }) => {
      // Callback function to execute when mutations are observed
      const callback = (mutationList) => {
        for (const mutation of mutationList) {
          if (mutation.type === 'childList') {
            if (mutation.addedNodes.length > 0) {
              resolve(targetFrame);
            }
          }
        }
      };

      // Create an observer instance linked to the callback function
      const observer = new MutationObserver(callback);

      // Start observing the target node for configured mutations
      observer.observe(contentDocument.body, config);
      contentWindow.onclose = () => {
        observer.disconnect();
      };

      return observer;
    },
    []
  );

  const getHTMLNode = useCallback((targetDocument = document) => {
    return targetDocument.head.parentNode as HTMLHtmlElement;
  }, []);

  /**
   * Creates a target for printing
   * injects styles and renders the content from the target ref,
   * and returns the created iframe
   * @returns {Promise<HTMLIFrameElement>}
   */
  const createTarget = useCallback(async () => {
    // return a new promise
    return new Promise<HTMLIFrameElement>((resolve, reject) => {
      void (async () => {
        try {
          const targetFrame = createTargetFrame();
          const bodyFragment = new DocumentFragment();

          const rootDiv = await insertTarget();
          bodyFragment.appendChild(rootDiv);

          const styles = await fetchStyles();

          targetFrame.addEventListener('load', async function () {
            const { contentWindow, contentDocument } = targetFrame;

            if (!contentWindow || !contentDocument) return;

            await injectStyles(contentDocument);

            targetIframeRef.current = this.contentWindow;
            const htmlNode = getHTMLNode();
            const contentDocumentHTMLElement = getHTMLNode(contentDocument);
            contentDocumentHTMLElement.dataset.mantineColorScheme =
              htmlNode.dataset.mantineColorScheme;
            contentDocument.head.appendChild(styles);
            // Double requestAnimationFrame to ensure the styles are loaded
            // the first requestAnimationFrame is to ensure the styles are added to the document head
            // the second requestAnimationFrame is to ensure the styles are loaded /applied to the document
            await new Promise((resolve) =>
              contentWindow.requestAnimationFrame(() =>
                contentWindow.requestAnimationFrame(resolve)
              )
            );
            // Once styles are loaded and added to the document head, we can create a mutation observer to detect when the target frame has loaded the content
            createFrameObserver({
              resolve,
              targetFrame,
              contentDocument,
              contentWindow,
            });

            contentDocument.body.appendChild(bodyFragment);

            /**
             * If the content document body has elements with the printty element class id
             * We can now add spacing between them to prevent them from running into the margins
             * and to ensure that the pages are not fragmented
             */
            const elements = contentDocument.body.querySelectorAll(`.${PRINTTY_ELEMENT_CLASS_ID}`);

            if (elements.length > 0) {
              const pageHeight = frameSizeByOrientation(orientation).pixels.height;
              createDynamicSpacing(elements, pageHeight, gap, debug, contentDocument);
            }
          });

          document.body.appendChild(targetFrame);
        } catch {
          reject(new Error('Error creating print target'));
        }
      })();
    });

    // All dependencies must be static, so we can add them all
  }, [
    createTargetFrame,
    insertTarget,
    fetchStyles,
    injectStyles,
    createFrameObserver,
    getHTMLNode,
    orientation,
    gap,
    debug,
  ]);

  // Cleanup the iFrame
  useEffect(() => {
    return () => {
      cleanupTargetFrame();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Do not want to run this on every render, only on unmount
  }, []);

  return {
    createTarget,
    targetRef,
    cleanupTargetFrame,
    targetIframeRef,
    targetReactRef,
    setTargetReactRef,
  };
};

export { useCreatePrinttyTarget };
